/**
 * Reconciles a list of indices from a previous ordering to a new ordering,
 * using entity IDs as the stable identity between the two orderings.
 *
 * Indices whose corresponding ID no longer exists in the new ordering
 * are silently dropped. The result is sorted in ascending order.
 *
 * @example
 * ```ts
 * // Entities ['a', 'b', 'c'] reordered to ['c', 'a', 'b']
 * reconcileIndices([0, 2], ['a', 'b', 'c'], new Map([['c', 0], ['a', 1], ['b', 2]]));
 * // → [0, 1]
 * //   index 0 ('a') → new index 1
 * //   index 2 ('c') → new index 0
 * //   sorted: [0, 1]
 *
 * // Entity 'b' was deleted
 * reconcileIndices([0, 1, 2], ['a', 'b', 'c'], new Map([['a', 0], ['c', 1]]));
 * // → [0, 1]
 * //   index 1 ('b') not found in new ordering → dropped
 * ```
 */
export function reconcileIndices(
  prevIndices: number[],
  prevIds: string[],
  nextLookup: Map<string, number>
): number[] {
  const result: number[] = [];

  for (const idx of prevIndices) {
    const id = prevIds[idx];

    if (id === undefined) {
      continue;
    }

    const newIdx = nextLookup.get(id);

    if (newIdx !== undefined) {
      result.push(newIdx);
    }
  }

  return result.sort((a, b) => a - b);
}
